import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Paper,
  Button,
  Typography,
  Divider,
  CircularProgress,
  TextField,
  Box,
  makeStyles,
  Chip,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import { getBackendUrl } from "../../config";

// ---- Canvas de assinatura sem dependências externas ----
const SignaturePad = forwardRef(function SignaturePad({ width, height, penColor }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasStrokes = useRef(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    drawing.current = true;
    hasStrokes.current = true;
  }, []);

  const draw = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = penColor || "#1a237e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }, [penColor]);

  const stopDraw = useCallback((e) => {
    if (e) e.preventDefault();
    drawing.current = false;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Preencher fundo branco
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    canvas.addEventListener("mousedown", startDraw);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseleave", stopDraw);
    canvas.addEventListener("touchstart", startDraw, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", stopDraw, { passive: false });

    return () => {
      canvas.removeEventListener("mousedown", startDraw);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseleave", stopDraw);
      canvas.removeEventListener("touchstart", startDraw);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDraw);
    };
  }, [startDraw, draw, stopDraw]);

  useImperativeHandle(ref, () => ({
    isEmpty: () => !hasStrokes.current,
    clear: () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      hasStrokes.current = false;
    },
    toDataURL: (type) => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL(type || "image/png") : "";
    },
  }));

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: "block", cursor: "crosshair", touchAction: "none" }}
    />
  );
});
// ---- fim SignaturePad ----

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: theme.spacing(3, 2),
  },
  container: {
    maxWidth: 900,
    margin: "0 auto",
  },
  header: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
  },
  contractBody: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    "& img": { maxWidth: "100%" },
  },
  clauseTitle: {
    fontWeight: "bold",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  signersList: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  signerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #eee",
  },
  signForm: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  canvasWrap: {
    border: "2px solid #ddd",
    borderRadius: 4,
    background: "#fff",
    display: "inline-block",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  successBox: {
    padding: theme.spacing(3),
    background: "#e8f5e9",
    border: "1px solid #a5d6a7",
    borderRadius: 8,
    textAlign: "center",
  },
}));

export default function AssinarContrato() {
  const classes = useStyles();
  const { slug } = useParams();
  const signerIdentifier = new URLSearchParams(window.location.search).get("signer") || "";

  const sigPadRef = useRef(null);
  const [contrato, setContrato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [effectiveSigner, setEffectiveSigner] = useState(signerIdentifier);

  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedSuccess, setSignedSuccess] = useState(false);

  const backendUrl = getBackendUrl();

  useEffect(() => {
    axios
      .get(`${backendUrl}/contrato/${slug}`)
      .then((r) => setContrato(r.data))
      .catch((e) => setError(e?.response?.data?.error || "Contrato não encontrado."))
      .finally(() => setLoading(false));
  }, [slug, backendUrl]);

  const currentSigner = contrato
    ? (contrato.signers || []).find(
        (s) => s.email === effectiveSigner || s.id === effectiveSigner
      )
    : null;

  const handleIdentifyEmail = (e) => {
    e.preventDefault();
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;
    const matched = (contrato?.signers || []).find(
      (s) => s.email?.toLowerCase() === trimmed
    );
    if (!matched) {
      setError("Este e-mail não consta na lista de signatários deste contrato.");
      return;
    }
    setError("");
    setEffectiveSigner(trimmed);
  };

  const handleRequestCode = async () => {
    setSendingCode(true);
    try {
      await axios.post(`${backendUrl}/contrato/${slug}/request-code`, { signerIdentifier: effectiveSigner });
      setCodeSent(true);
    } catch (e) {
      setError(e?.response?.data?.error || "Erro ao enviar código.");
    } finally {
      setSendingCode(false);
    }
  };

  const handleSign = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      setError("Por favor, desenhe sua assinatura no campo acima.");
      return;
    }
    if (contrato.requireVerificationCode && !verificationCode) {
      setError("Informe o código de verificação.");
      return;
    }
    const signatureData = sigPadRef.current.toDataURL("image/png");
    setSigning(true);
    setError("");
    try {
      await axios.post(`${backendUrl}/contrato/${slug}/sign`, {
        signerIdentifier: effectiveSigner,
        signatureData,
        verificationCode: contrato.requireVerificationCode ? verificationCode : undefined,
      });
      setSignedSuccess(true);
    } catch (e) {
      setError(e?.response?.data?.error || "Erro ao registrar assinatura.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error && !contrato) {
    return (
      <Box className={classes.page}>
        <Box className={classes.container}>
          <Paper style={{ padding: 32, textAlign: "center" }}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  const logoSrc = contrato.logoUrl
    ? contrato.logoUrl.startsWith("/public")
      ? backendUrl + contrato.logoUrl
      : contrato.logoUrl
    : null;

  const canvasWidth = Math.min(typeof window !== "undefined" ? window.innerWidth - 80 : 500, 500);

  return (
    <Box className={classes.page}>
      {contrato.cssStyle && (
        <style dangerouslySetInnerHTML={{ __html: contrato.cssStyle }} />
      )}

      <Box className={classes.container}>
        {/* Cabeçalho */}
        <Paper
          className={classes.header}
          style={{ background: contrato.headerColor || "#1e293b", color: "#fff" }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            {logoSrc && (
              <img src={logoSrc} alt="Logo" style={{ maxHeight: 60, maxWidth: 180 }} />
            )}
            <Box>
              <Typography variant="h5">{contrato.title}</Typography>
              {contrato.dueDate && (
                <Typography variant="body2" style={{ opacity: 0.8 }}>
                  Vencimento:{" "}
                  {new Date(contrato.dueDate + "T00:00:00").toLocaleDateString("pt-BR")}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Corpo */}
        <Paper className={classes.contractBody}>
          {contrato.htmlContent ? (
            <div dangerouslySetInnerHTML={{ __html: contrato.htmlContent }} />
          ) : null}

          {contrato.clauses && contrato.clauses.length > 0 && (
            <Box mt={contrato.htmlContent ? 2 : 0}>
              {contrato.clauses.map((cl, idx) => (
                <Box key={cl.id || idx} mb={2}>
                  <Typography className={classes.clauseTitle}>{cl.title}</Typography>
                  <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                    {cl.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {!contrato.htmlContent && (!contrato.clauses || contrato.clauses.length === 0) && (
            <Typography color="textSecondary" style={{ fontStyle: "italic" }}>
              Contrato sem conteúdo definido.
            </Typography>
          )}

          <Box mt={3} textAlign="right">
            <Button variant="outlined" size="small" onClick={() => window.print()}>
              Imprimir
            </Button>
          </Box>
        </Paper>

        {/* Signatários */}
        <Paper className={classes.signersList}>
          <Typography variant="h6" gutterBottom>Signatários</Typography>
          {(contrato.signers || []).map((s, idx) => (
            <Box key={s.id || idx} className={classes.signerRow}>
              {s.signed ? (
                <CheckCircleIcon style={{ color: "#2e7d32" }} />
              ) : (
                <HourglassEmptyIcon style={{ color: "#e65100" }} />
              )}
              <Box flexGrow={1}>
                <Typography variant="body1">
                  <strong>{s.name}</strong>
                  {s.role ? ` — ${s.role}` : ""}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {s.email}
                  {s.signedAt
                    ? ` · Assinou em ${new Date(s.signedAt).toLocaleString("pt-BR")}`
                    : ""}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={s.signed ? "Assinado" : "Pendente"}
                style={
                  s.signed
                    ? { backgroundColor: "#2e7d32", color: "#fff" }
                    : { backgroundColor: "#e65100", color: "#fff" }
                }
              />
            </Box>
          ))}
        </Paper>

        {/* Formulário de assinatura */}
        {signedSuccess ? (
          <Paper className={classes.successBox}>
            <CheckCircleIcon style={{ fontSize: 48, color: "#2e7d32" }} />
            <Typography variant="h6" style={{ color: "#2e7d32", marginTop: 8 }}>
              Assinatura registrada com sucesso!
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              Obrigado por assinar o contrato "{contrato.title}".
            </Typography>
          </Paper>
        ) : !effectiveSigner ? (
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>Identificar-se para assinar</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Informe o e-mail cadastrado como signatário deste contrato.
            </Typography>
            {error && (
              <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>
                {error}
              </Typography>
            )}
            <form onSubmit={handleIdentifyEmail} style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <TextField
                label="Seu e-mail"
                type="email"
                variant="outlined"
                size="small"
                value={emailInput}
                onChange={(e) => { const v = e.target.value; setEmailInput(v); setError(""); }}
                style={{ minWidth: 280 }}
                required
              />
              <Button type="submit" variant="contained" color="primary">
                Continuar
              </Button>
            </form>
          </Paper>
        ) : !currentSigner ? (
          <Paper style={{ padding: 24 }}>
            <Typography color="textSecondary">
              O seu e-mail não consta na lista de signatários deste contrato.
            </Typography>
          </Paper>
        ) : currentSigner.signed ? (
          <Paper style={{ padding: 24, background: "#e8f5e9" }}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <CheckCircleIcon style={{ color: "#2e7d32" }} />
              <Typography>
                Você já assinou este contrato em{" "}
                {new Date(currentSigner.signedAt).toLocaleString("pt-BR")}.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Paper className={classes.signForm}>
            <Typography variant="h6" gutterBottom>
              Assinar como {currentSigner.name}
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            {error && (
              <Box mb={2}>
                <Typography color="error" variant="body2">{error}</Typography>
              </Box>
            )}

            {/* Código de verificação */}
            {contrato.requireVerificationCode && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Código de Verificação
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Um código de 6 dígitos será enviado para seu e-mail/WhatsApp.
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRequestCode}
                    disabled={sendingCode || codeSent}
                  >
                    {sendingCode ? (
                      <CircularProgress size={16} />
                    ) : codeSent ? (
                      "Código enviado"
                    ) : (
                      "Solicitar código"
                    )}
                  </Button>
                  {codeSent && (
                    <TextField
                      size="small"
                      label="Código recebido"
                      value={verificationCode}
                      onChange={(e) => { const v = e.target.value; setVerificationCode(v); }}
                      inputProps={{ maxLength: 6, style: { letterSpacing: 4, fontSize: 18 } }}
                      style={{ width: 140 }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Pad de assinatura */}
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>Sua Assinatura</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Desenhe sua assinatura no espaço abaixo:
              </Typography>
              <Box className={classes.canvasWrap}>
                <SignaturePad
                  ref={sigPadRef}
                  width={canvasWidth}
                  height={180}
                  penColor="#1a237e"
                />
              </Box>
              <Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => sigPadRef.current && sigPadRef.current.clear()}
                >
                  Limpar
                </Button>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSign}
              disabled={
                signing ||
                (contrato.requireVerificationCode && !codeSent)
              }
            >
              {signing ? <CircularProgress size={20} /> : "Assinar Contrato"}
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
